import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import type WaveSurfer from "wavesurfer.js"
import type { RegionParams } from "wavesurfer.js/dist/plugins/regions.js"

import type { WavesurferPeaks } from "@/hooks/usePrecomputedPeaks"

export function isAbortedAudioLoad(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" ||
      error.message.includes("The operation was aborted"))
  )
}

export function loadWaveformSource(
  wavesurfer: WaveSurfer,
  signedUrl: string,
  precomputedPeaks: WavesurferPeaks | null | undefined,
  audioDuration: number | null | undefined,
): Promise<void> {
  return precomputedPeaks
    ? wavesurfer.load(signedUrl, precomputedPeaks, audioDuration ?? undefined)
    : wavesurfer.load(signedUrl)
}

export function pauseForRouteChange(
  wavesurfer: WaveSurfer | null,
  shallow: boolean,
) {
  if (shallow || !wavesurfer) {
    return
  }
  try {
    wavesurfer.pause()
  } catch {
    // Wavesurfer can already be tearing down during route transitions.
  }
}

export function useAudioWaveformLifecycle({
  audioDuration,
  currentTime,
  isPlaying,
  isReady,
  loadablePrecomputedPeaks,
  onIsPlayingChange,
  onReady,
  onTimeUpdate,
  playRange,
  playbackRate,
  regions,
  signedUrl,
  time,
  togglePlayPauseRequestId,
  wavesurfer,
}: {
  audioDuration?: number | null
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  loadablePrecomputedPeaks?: WavesurferPeaks | null
  onIsPlayingChange?: (isPlaying: boolean) => void
  onReady?: () => void
  onTimeUpdate?: (currentTimeSeconds: number) => void
  playRange?: { start: number; end: number; requestId: number }
  playbackRate: number
  regions?: RegionParams[]
  signedUrl?: string
  time?: number
  togglePlayPauseRequestId?: number
  wavesurfer: WaveSurfer | null
}) {
  const router = useRouter()
  const [loadError, setLoadError] = useState<Error | null>(null)
  const loadOptionsRef = useRef({
    audioDuration,
    precomputedPeaks: loadablePrecomputedPeaks,
  })
  const lastPlayRangeIdRef = useRef<number | undefined>(undefined)
  const lastTogglePlayPauseRequestIdRef = useRef<number | undefined>(
    togglePlayPauseRequestId,
  )

  const onPlayPause = useCallback(() => {
    if (!wavesurfer) {
      return
    }
    void wavesurfer.playPause()
    onIsPlayingChange?.(!isPlaying)
  }, [wavesurfer, isPlaying, onIsPlayingChange])

  useEffect(() => {
    const handleRouteChange = (
      _url: string,
      { shallow }: { shallow: boolean },
    ) => {
      pauseForRouteChange(wavesurfer, shallow)
    }
    router.events.on("routeChangeStart", handleRouteChange)
    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [router.events, wavesurfer])

  useEffect(() => {
    if (togglePlayPauseRequestId == null) {
      return
    }
    if (lastTogglePlayPauseRequestIdRef.current === togglePlayPauseRequestId) {
      return
    }
    lastTogglePlayPauseRequestIdRef.current = togglePlayPauseRequestId
    if (wavesurfer && isReady) {
      onPlayPause()
    }
  }, [togglePlayPauseRequestId, wavesurfer, isReady, onPlayPause])

  useEffect(() => {
    if (isReady && wavesurfer) {
      wavesurfer.setPlaybackRate(playbackRate)
    }
  }, [isReady, wavesurfer, playbackRate, regions])

  useEffect(() => {
    loadOptionsRef.current = {
      audioDuration,
      precomputedPeaks: loadablePrecomputedPeaks,
    }
  }, [audioDuration, loadablePrecomputedPeaks])

  useEffect(() => {
    if (!signedUrl || !wavesurfer) {
      return
    }
    setLoadError(null)
    let isCurrent = true
    const handleError = (error: Error) => {
      if (isCurrent && !isAbortedAudioLoad(error)) {
        setLoadError(error)
      }
    }
    wavesurfer.on("error", handleError)
    const loadOptions = loadOptionsRef.current
    void loadWaveformSource(
      wavesurfer,
      signedUrl,
      loadOptions.precomputedPeaks,
      loadOptions.audioDuration,
    ).catch((error: unknown) => {
      if (isCurrent && !isAbortedAudioLoad(error)) {
        setLoadError(error instanceof Error ? error : new Error(String(error)))
      }
    })
    return () => {
      isCurrent = false
      wavesurfer.un("error", handleError)
    }
  }, [signedUrl, wavesurfer])

  useEffect(() => {
    if (isReady && wavesurfer && typeof time === "number") {
      wavesurfer.setTime(time)
    }
  }, [time, isReady, wavesurfer])

  useEffect(() => {
    if (!playRange || !wavesurfer || !isReady) {
      return
    }
    if (lastPlayRangeIdRef.current === playRange.requestId) {
      return
    }
    lastPlayRangeIdRef.current = playRange.requestId
    wavesurfer.setTime(playRange.start)
    void wavesurfer.play()

    const handleTimeUpdate = (nextTime: number) => {
      if (nextTime >= playRange.end) {
        wavesurfer.pause()
      }
    }
    wavesurfer.on("timeupdate", handleTimeUpdate)
    return () => {
      wavesurfer.un("timeupdate", handleTimeUpdate)
    }
  }, [playRange, wavesurfer, isReady])

  useEffect(() => {
    if (isReady) {
      onReady?.()
    }
  }, [isReady, onReady])

  useEffect(() => {
    if (isPlaying) {
      onTimeUpdate?.(currentTime)
    }
  }, [currentTime, isPlaying, onTimeUpdate])

  return { loadError, onPlayPause }
}
